// src/components/trades/TradeCenter.js
import React, { useState, useEffect } from 'react';
import { dynastyTheme } from '../../services/colorService';
import TeamPageHeader from '../common/TeamPageHeader';
import { ArrowRightLeft, DollarSign } from 'lucide-react';
import { leaguesAPI, playersAPI } from '../../services/apiService';
import ProposeTradeTab from './ProposeTradeTab';
import CashLedger from './CashLedger';
import DraftPicksTab from './DraftPicksTab';
import { useCommissioner } from '../../contexts/CommissionerContext';
import CommissionerToggle from '../commissioner/CommissionerToggle';
import CommissionerModeBar from '../commissioner/CommissionerModeBar';
import TabButton from './components/TabButton';
import InboxTab from './components/InboxTab';
import SentTab from './components/SentTab';
import PendingTab from './components/PendingTab';
// DEFECT 2 Phase 1 — accept-side placement cascade reuses the same canonical
// waiver primitives as the propose-side cascade in ProposeTradeTab.
import DropDesignationModal from '../modals/DropDesignationModal';
import { analyzeRosterCapacity, canClaimWaiverPlayer } from '../../utils/RosterCapacityUtils';

/**
 * TradeCenter Component - PHASE 5 ENHANCED
 * Integrated into TeamHomeDashboard under "My Team" section
 * Handles viewing and proposing trades with enriched player data
 */
const TradeCenter = ({ leagueId, userTeam, league, initialTradePlayer, initialTab, initialTradeState, highlightTradeId = null }) => {
  const { isCommissionerMode, activeTeamId, getTargetTeamId, isCommissioner } = useCommissioner();
  const [activeTab, setActiveTab] = useState(initialTab || 'propose');
  const [trades, setTrades] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPicksForTrade, setSelectedPicksForTrade] = useState([]);
  const [submitTrade, setSubmitTrade] = useState(null);
  
  // Persistent trade state - survives tab switches
  const [tradeState, setTradeState] = useState({
    selectedTeamId: '',
    iGive: { players: [], picks: [], cash: 0 },
    iGet: { players: [], picks: [], cash: 0 },
    notes: ''
  });

  // Initialize trade state with pre-selected player from MyRoster
  useEffect(() => {
    if (initialTradePlayer) {
      console.log('🎯 Initializing trade with player:', initialTradePlayer);
      
      if (initialTradePlayer.isMyPlayer) {
        // Add to "I Give" side
        setTradeState(prev => ({
          ...prev,
          iGive: {
            ...prev.iGive,
            players: [initialTradePlayer.player]
          }
        }));
      } else {
        // Add to "I Get" side and set their team as selected
        setTradeState(prev => ({
          ...prev,
          selectedTeamId: initialTradePlayer.teamId || '',
          iGet: {
            ...prev.iGet,
            players: [initialTradePlayer.player]
          }
        }));
      }
      
      // Make sure we're on the Propose tab
      setActiveTab('propose');
    }
  }, [initialTradePlayer]);

  // Initialize from optimizer suggestion (full trade state)
  useEffect(() => {
    if (initialTradeState) {
      console.log('🎯 Initializing trade from optimizer suggestion:', initialTradeState);
      setTradeState({
        selectedTeamId: initialTradeState.selectedTeamId || '',
        iGive: initialTradeState.iGive || { players: [], picks: [], cash: 0 },
        iGet: initialTradeState.iGet || { players: [], picks: [], cash: 0 },
        notes: initialTradeState.notes || '',
      });
      setActiveTab('propose');
    }
  }, [initialTradeState]);

  useEffect(() => {
    if (leagueId) {
      fetchTrades();
    }
  }, [leagueId, activeTeamId, isCommissionerMode]);

  // Notification deep-link (Bible § 4.5.12.2): once trades load, scroll to + highlight
  // the trade named by ?tradeId (the tab is already selected via initialTab). No-op
  // when absent — no wrong-row fallback.
  useEffect(() => {
    if (!highlightTradeId || !trades) return;
    const el = document.getElementById(`trade-${highlightTradeId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightTradeId, trades, activeTab]);

  const fetchTrades = async () => {
    try {
      setLoading(true);
      setError(null);

      // In commissioner mode, fetch trades for the selected team
      const targetTeamId = isCommissionerMode ? activeTeamId : null;
      const response = await leaguesAPI.getMyTradeProposals(leagueId, targetTeamId);
      
      if (response.success) {
        setTrades(response);
        console.log('✅ Trades loaded with enriched player data from backend:', response);
      }
    } catch (err) {
      console.error('Error fetching trades:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };



  // DEFECT 2 Phase 1 — accept-side placement cascade state. When the receiver
  // clicks Accept on an incoming trade, if the trade brings in players
  // (trade.they_give.players non-empty), we fire a sequential DropDesignation-
  // Modal cascade to gather receiver_placements. On the final modal, the
  // accept POST fires with the accumulated placements.
  const [acceptCascade, setAcceptCascade] = useState({
    active: false,
    trade: null,            // the trade record being accepted
    incomingPlayers: [],    // canonical player objects to place
    currentIdx: 0,
    accumulated: [],
    myRoster: null,         // loaded lazily when cascade opens
    capacityAnalysis: null,
  });

  const buildPlacementFromModal = (incomingPlayer, dropLeaguePlayerId) => {
    const incoming_player_mlb_id = incomingPlayer?.ids?.mlb;
    const incoming_player_league_player_id = incomingPlayer?.ids?.league_player;
    if (dropLeaguePlayerId) {
      return {
        incoming_player_mlb_id,
        incoming_player_league_player_id,
        action: 'replace',
        target_slot: null,  // backend derives from drop player's slot
        drop_league_player_id: dropLeaguePlayerId,
      };
    }
    return {
      incoming_player_mlb_id,
      incoming_player_league_player_id,
      action: 'bench',
      target_slot: null,
      drop_league_player_id: null,
    };
  };

  const submitAcceptWithPlacements = async (tradeId, placements) => {
    try {
      await leaguesAPI.respondToTrade(
        leagueId,
        tradeId,
        'accept',
        null,
        isCommissionerMode,
        placements && placements.length > 0 ? placements : null,
      );
      await fetchTrades();
    } catch (err) {
      const detail =
        err?.response?.data?.error?.message ||
        err?.response?.data?.detail ||
        err?.message ||
        'Unknown error';
      alert('Failed to accept trade: ' + detail);
    }
  };

  const handleAcceptTrade = async (tradeId) => {
    if (!window.confirm('Accept this trade? This action cannot be undone.')) return;

    // Locate the trade record (with enriched players) from state.
    const trade = (trades?.proposed_to_me || []).find(t => t.trade_id === tradeId);
    const incomingPlayers = (trade?.they_give?.players) || [];

    // No incoming players for the receiver → direct accept; backend's Phase
    // 0 auto-assign covers the receiver side if needed.
    if (incomingPlayers.length === 0) {
      await submitAcceptWithPlacements(tradeId, null);
      return;
    }

    // Load receiver (= me) roster + capacity analysis for the cascade.
    try {
      const myTeamId = userTeam?.team_id;
      if (!myTeamId) {
        // Should never happen — accept requires the user to own a team.
        alert('Could not determine your team. Refresh and try again.');
        return;
      }
      const rosterResp = await leaguesAPI.getTeamRosterCanonical(leagueId, myTeamId);
      const myRoster = rosterResp?.players || [];
      const capacityAnalysis = league && myRoster.length > 0
        ? analyzeRosterCapacity(league, myRoster)
        : null;
      setAcceptCascade({
        active: true,
        trade,
        incomingPlayers,
        currentIdx: 0,
        accumulated: [],
        myRoster,
        capacityAnalysis,
      });
    } catch (err) {
      console.error('Failed to load roster for accept cascade:', err);
      alert('Could not load your roster for placement. Try again in a moment.');
    }
  };

  const handleAcceptCascadeConfirm = async (dropLeaguePlayerId) => {
    const incoming = acceptCascade.incomingPlayers[acceptCascade.currentIdx];
    const placement = buildPlacementFromModal(incoming, dropLeaguePlayerId);
    const newAccumulated = [...acceptCascade.accumulated, placement];
    const nextIdx = acceptCascade.currentIdx + 1;

    if (nextIdx >= acceptCascade.incomingPlayers.length) {
      const tradeId = acceptCascade.trade?.trade_id;
      setAcceptCascade({
        active: false, trade: null, incomingPlayers: [],
        currentIdx: 0, accumulated: [], myRoster: null, capacityAnalysis: null,
      });
      if (tradeId) {
        await submitAcceptWithPlacements(tradeId, newAccumulated);
      }
    } else {
      setAcceptCascade(prev => ({
        ...prev,
        currentIdx: nextIdx,
        accumulated: newAccumulated,
      }));
    }
  };

  const handleAcceptCascadeClose = () => {
    setAcceptCascade({
      active: false, trade: null, incomingPlayers: [],
      currentIdx: 0, accumulated: [], myRoster: null, capacityAnalysis: null,
    });
  };

  const handleRejectTrade = async (tradeId) => {
    const reason = window.prompt('Reason for rejecting? (optional)');
    
    try {
      await leaguesAPI.respondToTrade(leagueId, tradeId, 'reject', reason, isCommissionerMode);
      await fetchTrades(); // Refresh
    } catch (err) {
      alert('Failed to reject trade: ' + err.message);
    }
  };

  const handleCancelTrade = async (tradeId) => {
    if (!window.confirm('Cancel this trade proposal?')) return;
    
    try {
      await leaguesAPI.cancelTrade(leagueId, tradeId);
      await fetchTrades(); // Refresh
    } catch (err) {
      alert('Failed to cancel trade: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div className={dynastyTheme.components.card.base}>
        <div className="p-6">
          <h3 className={dynastyTheme.components.heading.h3}>
            <ArrowRightLeft className="inline-block w-5 h-5 mr-2" />
            Trade Center
          </h3>
          <div className={`${dynastyTheme.classes.text.neutralLight} text-center py-12`}>
            <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${dynastyTheme.classes.border.primary} mx-auto`}></div>
            <p className="mt-4">Loading trades...</p>
          </div>
        </div>
      </div>
    );
  }

  const incomingTrades = trades?.proposed_to_me || [];
  const outgoingTrades = trades?.proposed_by_me || [];
  const waitingApproval = trades?.waiting_commissioner_approval || [];

  return (
    <>
      <TeamPageHeader
        title="Trade Center"
        logoUrl={userTeam?.team_logo_url}
        teamName={userTeam?.team_name}
        teamColors={userTeam?.team_colors}
        teamMotto={userTeam?.team_motto}
        isCommissioner={isCommissioner}
        actions={
          <CommissionerToggle
            leagueId={leagueId}
            userIsCommissioner={league?.role === 'commissioner' || isCommissioner}
          />
        }
      />
      {isCommissionerMode && (
        <CommissionerModeBar leagueId={leagueId} />
      )}
      <div className={dynastyTheme.components.card.base}>
      <div className="p-4">
        {/* Tab Navigation */}
        <div className={`flex items-center border-b ${dynastyTheme.classes.border.neutral} mb-4 overflow-x-auto`}>
          <TabButton
            active={activeTab === 'propose'}
            onClick={() => setActiveTab('propose')}
            label="Propose Trade"
            icon={<ArrowRightLeft className="w-4 h-4" />}
          />
          <TabButton
            active={activeTab === 'picks'}
            onClick={() => setActiveTab('picks')}
            label="Draft Picks"
          />
          <TabButton
            active={activeTab === 'ledger'}
            onClick={() => setActiveTab('ledger')}
            label="Cash Ledger"
            icon={<DollarSign className="w-4 h-4" />}
          />
          <TabButton
            active={activeTab === 'inbox'}
            onClick={() => setActiveTab('inbox')}
            label="Inbox"
            count={incomingTrades.length}
            highlight={incomingTrades.length > 0}
          />
          <TabButton
            active={activeTab === 'sent'}
            onClick={() => setActiveTab('sent')}
            label="Sent"
            count={outgoingTrades.length}
          />
          {waitingApproval.length > 0 && (
            <TabButton
              active={activeTab === 'pending'}
              onClick={() => setActiveTab('pending')}
              label="Pending Approval"
              count={waitingApproval.length}
              highlight={true}
            />
          )}
          <div className="ml-auto flex-shrink-0 pb-2 pl-4">
            <button
              onClick={() => {
                if (submitTrade) submitTrade();
                else setActiveTab('propose');
              }}
              className={dynastyTheme.utils.getComponent('button', 'primary', 'sm')}
            >
              Propose Trade
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'inbox' && (
          <InboxTab
            trades={incomingTrades}
            onAccept={handleAcceptTrade}
            onReject={handleRejectTrade}
            highlightTradeId={highlightTradeId}
          />
        )}

        {activeTab === 'sent' && (
          <SentTab
            trades={outgoingTrades}
            onCancel={handleCancelTrade}
            highlightTradeId={highlightTradeId}
          />
        )}

        {activeTab === 'pending' && (
          <PendingTab
            trades={waitingApproval}
            highlightTradeId={highlightTradeId}
          />
        )}

        {activeTab === 'propose' && (
          <ProposeTradeTab
            leagueId={leagueId}
            userTeam={userTeam}
            league={league}
            preSelectedPicks={selectedPicksForTrade}
            tradeState={tradeState}
            onTradeStateChange={setTradeState}
            onSubmitReady={(fn) => setSubmitTrade(() => fn)}
            isCommissionerMode={isCommissionerMode}
            commissionerTeamId={activeTeamId}
            onTradeProposed={() => {
              fetchTrades();
              setActiveTab('sent');
              setSelectedPicksForTrade([]); // Clear selected picks after trade
              // Reset trade state after successful proposal
              setTradeState({
                selectedTeamId: '',
                iGive: { players: [], picks: [], cash: 0 },
                iGet: { players: [], picks: [], cash: 0 },
                notes: ''
              });
            }}
          />
        )}
        
        {activeTab === 'picks' && (
          <DraftPicksTab
            leagueId={leagueId}
            userTeam={userTeam}
            league={league}
            selectedPicks={[...tradeState.iGive.picks, ...tradeState.iGet.picks]}
            onPickToggle={(pick) => {
              const isMyPick = pick.current_owner_team_id === userTeam.team_id;
              if (isMyPick) {
                const isSelected = tradeState.iGive.picks.some(p => p.pick_id === pick.pick_id);
                setTradeState(prev => ({
                  ...prev,
                  iGive: {
                    ...prev.iGive,
                    picks: isSelected
                      ? prev.iGive.picks.filter(p => p.pick_id !== pick.pick_id)
                      : [...prev.iGive.picks, pick]
                  }
                }));
              } else {
                const isSelected = tradeState.iGet.picks.some(p => p.pick_id === pick.pick_id);
                setTradeState(prev => ({
                  ...prev,
                  iGet: {
                    ...prev.iGet,
                    picks: isSelected
                      ? prev.iGet.picks.filter(p => p.pick_id !== pick.pick_id)
                      : [...prev.iGet.picks, pick]
                  }
                }));
              }
            }}
          />
        )}
        
        {activeTab === 'ledger' && (
          <CashLedger
            leagueId={leagueId}
            teamId={getTargetTeamId(userTeam?.team_id)}
            teamName={userTeam?.team_name || 'Your Team'}
          />
        )}
      </div>
    </div>

    {/* DEFECT 2 Phase 1 — receiver-side placement cascade. Fires after the
        user clicks Accept on an inbox trade with non-empty incoming players. */}
    {acceptCascade.active && acceptCascade.incomingPlayers[acceptCascade.currentIdx] && (() => {
      const incoming = acceptCascade.incomingPlayers[acceptCascade.currentIdx];
      const claimResult = acceptCascade.capacityAnalysis
        ? canClaimWaiverPlayer(incoming, acceptCascade.capacityAnalysis)
        : { hasActiveSlot: false, hasBenchSlot: false, requiresDrop: true };
      const isRosterFull = !!claimResult.requiresDrop;
      console.log('[TRADE-ACCEPT-DEBUG]', { hasLeague: !!league, incoming_position: incoming?.info?.position, incoming_eligible: incoming?.eligible_positions, position_requirements: league?.roster_settings?.position_requirements, bench_slots: league?.roster_settings?.bench_slots, capacityAnalysis: acceptCascade.capacityAnalysis, claimResult }); // REVERT AFTER TRADE DEBUG
      return (
        <DropDesignationModal
          isOpen={true}
          onClose={handleAcceptCascadeClose}
          onConfirm={handleAcceptCascadeConfirm}
          playerBeingAdded={incoming}
          currentRoster={acceptCascade.myRoster || []}
          leagueId={leagueId}
          context="trade_placement"
          isRosterFull={isRosterFull}
          hasActiveSlot={claimResult.hasActiveSlot}
          hasBenchSlot={claimResult.hasBenchSlot}
          waiverPriority={null}
          totalTeams={null}
          waiverExpiresAt={null}
        />
      );
    })()}
    </>
  );
};


export default TradeCenter;
